import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, CheckIcon, Sparkle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { useSubscriptionStore } from '@/stores/useSubscriptionStore';
import SEOHead from '@/components/seo/SEOHead';
import { useWaitlistStore } from '@/stores/useWaitlistStore';
import { WaitlistDialog } from '@/components/common/WaitlistDialog';

// Landing page components
import Features from '@/components/landing/Features';
import Caregivers from '@/components/landing/Caregivers';
import HowItWorks from '@/components/landing/HowItWorks';
import FAQSection from '@/components/landing/FAQSection';
import CtaSection from '@/components/landing/CtaSection';
import { Footer } from '@/components/layout/Footer';

const pricingStructuredData = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  "name": "Meduler",
  "applicationCategory": "HealthApplication",
  "operatingSystem": "Web, iOS, Android",
  "description": "Meduler is free to use for essential medication management. Meduler Plus unlocks advanced caregiver alerts, intelligent reminders, and medication tracking for $4.99/month.",
  "offers": [
    {
      "@type": "Offer",
      "name": "Meduler Starter",
      "price": "0",
      "priceCurrency": "USD",
      "description": "Free medication schedule, dual user profiles, offline access, calendar view — no sign-up required"
    },
    {
      "@type": "Offer",
      "name": "Meduler Plus Monthly",
      "price": "4.99",
      "priceCurrency": "USD",
      "description": "Medication tracking, intelligent reminders, advanced caregiver support, early access to new features"
    }
  ]
};

const PricingPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { plan, hasPlus, fetchSubscription } = useSubscriptionStore();
  
  const [isWaitlistOpen, setIsWaitlistOpen] = useState(false);
  
  const { 
    remainingSpots, 
    isUserWaitlisted,
    waitlistPosition,
  } = useWaitlistStore();
  
  // Features available in each plan
  const features = {
    free: [
      'Intuitive medication schedule',
      'Dual user profiles for caregivers',
      'Offline access',
      'Calendar view',
      'No sign up required'
    ],
    plus: [
      'Medication tracking',
      'Intelligent reminders',
      'Early access to new features',
      'Advanced caregiver support',
      'And more...',
    ]
  };

  const handleStartFree = () => {
    // For free tier, just navigate to app
    navigate('/');
    toast({
      title: 'Welcome to Meduler',
      description: 'You are using the free tier of Meduler.',
    });
  };

  const handleStartPlus = () => {
    // Navigate to the upgrade page or payment processing page
    navigate('/upgrade');
    toast({
      title: 'Upgrade to Meduler Plus',
      description: 'Unlock all premium features with Meduler Plus.',
    });
  };

  return (
    <div className="container mx-auto py-12 px-4">
      <SEOHead
        title="Pricing — Free & Plus Plans"
        description="Meduler is free to use. Meduler Plus unlocks advanced reminders, caregiver alerts, and medication tracking for $4.99/month."
        keywords="meduler pricing, medication app free plan, meduler plus cost, medication management subscription"
        canonicalUrl="https://www.meduler.com/pricing"
        image="https://www.meduler.com/social/og-image.png"
        structuredData={pricingStructuredData}
      />

      {/* Direct-answer paragraph for GEO/AEO */}
      <div className="sr-only">
        Meduler is free to use. The free Starter plan includes an intuitive medication schedule, dual user profiles for caregivers, offline access, and a calendar view — no sign-up required. Meduler Plus unlocks medication tracking, intelligent reminders, advanced caregiver support, and early access to new features for $4.99 per month.
      </div>

      <div className="text-center mb-24 md:mb-32 pb-6">
        <h1 className="text-4xl font-bold mb-4">Pricing</h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto pb-4">
          Choose the perfect plan for managing your medications
        </p>
        <p className="text-base text-gray-600 max-w-2xl mx-auto">
          Meduler is free to use for everyone. Upgrade to Plus for advanced features that help you and your care team stay on top of every dose.
        </p>
      </div>

      {/* Pricing Cards Container with custom arrangement to feature the Plus tier */}
      <div className="grid md:grid-cols-2 gap-8 gap-y-12 max-w-5xl mx-auto relative mt-12">
        {/* Free Tier */}
        <Card className="flex flex-col border-2 relative md:mt-8 shadow-sm dark:shadow-gray-800/20 transition-all duration-300 hover:shadow-md">
          {/* Most Popular Badge */}
          <div className="absolute -top-4 left-0 right-0 flex justify-center">
            <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white text-sm font-medium py-1 px-4 rounded-full shadow-md flex items-center gap-1">
              <Sparkle className="h-4 w-4" />
              Most Popular
              <Sparkle className="h-4 w-4" />
            </div>
          </div>
          <CardHeader className="text-center">
            <h2 className="text-2xl font-bold">Starter</h2>
            <p className="text-sm text-muted-foreground mt-2">
              For individuals just getting started—no credit card needed
            </p>
          </CardHeader>
          <CardContent className="flex-1">
            <div className="text-center mb-6">
              <div className="text-4xl font-bold">Free</div>
            </div>
            <ul className="space-y-3">
              {features.free.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <CheckIcon className="h-5 w-5 text-primary shrink-0 mr-2" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
          </CardContent>
          <CardFooter>
            <Button 
              onClick={handleStartFree} 
              className="w-full" 
              variant="outline"
            >
              Get Started
            </Button>
          </CardFooter>
        </Card>

        {/* Plus Tier - Enhanced with visual prominence */}
        <Card className="flex flex-col border-3 border-primary relative md:scale-110 md:-mt-4 md:mb-4 z-10 shadow-xl dark:shadow-purple-800/20 bg-gradient-to-b from-white to-purple-50 dark:from-gray-900 dark:to-purple-950/20 transition-all duration-300 hover:shadow-2xl">
          {/* Additional Support Badge */}
          <div className="absolute -top-4 left-0 right-0 flex justify-center">
            <div className="bg-gradient-to-r from-blue-500 to-blue-700 text-white text-sm font-medium py-1 px-4 rounded-full shadow-md flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              Additional Support
              <AlertCircle className="h-4 w-4" />
            </div>
          </div>
          
          <CardHeader className="text-center pt-8">
            <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-purple-800">Plus</h2>
            <p className="text-sm text-muted-foreground mt-2">
              Unlock additional capabilities of Meduler
            </p>
            
            <div className="mt-6 text-center bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border-2 border-purple-200 dark:border-purple-800">
              <div className="flex items-center justify-center">
                <div className="text-5xl font-extrabold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-purple-800">$4.99</div>
                <div className="text-lg text-muted-foreground ml-2">/month</div>
              </div>
              <div className="text-sm mt-3 text-gray-500 dark:text-gray-400">
                Join the waitlist now. No payment is required until Plus is available.
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex-1 pb-4">
            {/* Value proposition tag */}
            <div className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-200 text-xs font-medium py-2 px-3 rounded-lg mb-6 text-center">
              Powerful features to transform your medication management experience
            </div>

            <ul className="space-y-4">
              {features.plus.map((feature, index) => (
                <li key={index} className="flex items-start bg-white dark:bg-gray-800 p-3 rounded-lg shadow-sm transition-all duration-300 hover:shadow-md hover:scale-[1.02]">
                  <div className="bg-purple-100 dark:bg-purple-900/50 p-1.5 rounded-full mr-3">
                    <CheckIcon className="h-5 w-5 text-purple-600 dark:text-purple-400 shrink-0" />
                  </div>
                  <span className="text-gray-800 dark:text-gray-200 font-medium">{feature}</span>
                </li>
              ))}
            </ul>
            
            {/* Value indicator */}
            <div className="mt-6 bg-gradient-to-r from-purple-50 to-fuchsia-50 dark:from-purple-900/20 dark:to-fuchsia-900/20 p-3 rounded-lg border border-purple-100 dark:border-purple-800">
              <p className="text-sm text-center text-purple-800 dark:text-purple-200">
                <span className="font-semibold">Save 50+ hours</span> per year managing medications
              </p>
            </div>
          </CardContent>
          <CardFooter className="pb-8 pt-2 card-footer-email-section" id="plus-signup-section">
            <div className="w-full space-y-5">
              {isUserWaitlisted ? (
                <div className="w-full py-3 px-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-center">
                  <p className="text-green-700 dark:text-green-300 font-medium">
                    You're on the waitlist! #{waitlistPosition}
                  </p>
                  <p className="text-green-600 dark:text-green-400 text-sm mt-1">
                    We'll notify you when Plus is available
                  </p>
                </div>
              ) : (
                <Button
                  onClick={() => setIsWaitlistOpen(true)}
                  className="w-full rounded-full h-12 bg-gradient-to-r from-purple-600 to-purple-800 hover:from-purple-700 hover:to-purple-900 text-white font-semibold shadow-md"
                >
                  Sign in with Google to Join Waitlist
                </Button>
              )}

              <p className="text-xs text-center text-gray-500 dark:text-gray-400">
                No payment required. We'll notify you when Plus launches.
              </p>
            </div>
          </CardFooter>
        </Card>
      </div>

      <WaitlistDialog open={isWaitlistOpen} onOpenChange={setIsWaitlistOpen} />

      {/* Comparison table for GEO / AEO direct answers */}
      <section className="mt-24 max-w-3xl mx-auto" aria-labelledby="comparison-heading">
        <h2 id="comparison-heading" className="text-2xl font-bold text-center mb-8">Free vs. Plus — What's the difference?</h2>
        <div className="overflow-x-auto rounded-xl border border-purple-100 shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-purple-50">
                <th className="text-left p-4 font-semibold text-gray-700">Feature</th>
                <th className="text-center p-4 font-semibold text-gray-700">Starter (Free)</th>
                <th className="text-center p-4 font-semibold text-purple-700">Plus</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-purple-50">
              {[
                ['Medication schedule', true, true],
                ['Offline access', true, true],
                ['Dual user profiles', true, true],
                ['Calendar view', true, true],
                ['No sign-up required', true, true],
                ['Medication tracking & adherence', false, true],
                ['Intelligent reminders', false, true],
                ['Advanced caregiver alerts', false, true],
                ['Early access to new features', false, true],
              ].map(([feature, free, plus], i) => (
                <tr key={i} className="hover:bg-purple-50/50 transition-colors">
                  <td className="p-4 text-gray-700">{feature as string}</td>
                  <td className="p-4 text-center">{free ? <CheckIcon className="h-5 w-5 text-green-500 mx-auto" /> : <span className="text-gray-300">—</span>}</td>
                  <td className="p-4 text-center">{plus ? <CheckIcon className="h-5 w-5 text-purple-600 mx-auto" /> : <span className="text-gray-300">—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* FAQ Section for Pricing */}
      <section className="mt-20 max-w-3xl mx-auto" aria-labelledby="pricing-faq-heading">
        <h2 id="pricing-faq-heading" className="text-2xl font-bold text-center mb-8">Pricing Questions</h2>
        <div className="space-y-4">
          {[
            {
              q: "Is Meduler really free?",
              a: "Yes. Meduler's Starter plan is completely free with no credit card required. It includes a full medication schedule, offline access, dual user profiles, and a calendar view."
            },
            {
              q: "What does Meduler Plus cost?",
              a: "Meduler Plus is listed at $4.99 per month. Join the waitlist to be notified when Plus is available; no payment is required to join."
            },
            {
              q: "Can I cancel Meduler Plus at any time?",
              a: "Yes. You can cancel your Plus subscription at any time. You'll retain Plus access until the end of your current billing period."
            },
            {
              q: "What happens to my data if I downgrade from Plus to Free?",
              a: "Your medication data remains intact. You'll simply lose access to Plus-only features like advanced reminders and caregiver alerts, but your schedule and history stay safe."
            },
          ].map((faq, i) => (
            <details key={i} className="rounded-xl border border-purple-100 bg-white shadow-sm group">
              <summary className="p-5 font-semibold text-gray-800 cursor-pointer list-none flex justify-between items-center">
                {faq.q}
                <span className="text-purple-400 group-open:rotate-180 transition-transform">▼</span>
              </summary>
              <div className="px-5 pb-5 text-gray-600 text-sm leading-relaxed">{faq.a}</div>
            </details>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PricingPage;
